import { useEffect, useState } from 'preact/hooks';
import type { AtlasLink } from '../data/verse-atlas-links.js';
import {
  fetchBlurb,
  atlasDeepLink,
  currentReaderReturnTo,
  type AtlasDeepLinkOpts,
  type BlurbCacheEntry,
} from '../atlas/blurb-cache.js';
import { colorProfile } from '../state/color-profile.js';
import { recordAtlasJump } from '../state/engagement-store.js';

function atlasOpts(verseRef: string): AtlasDeepLinkOpts {
  const opts: AtlasDeepLinkOpts = { ref: verseRef, from: READER_PROVENANCE, palette: colorProfile.peek() };
  const back = currentReaderReturnTo(verseRef);
  if (back) opts.returnTo = back;
  return opts;
}

type Props = {
  link: AtlasLink;
  lang: string;
  text: string;
  verseRef: string;
  extraClass?: string;
};

const READER_PROVENANCE = (typeof window !== 'undefined'
  && (window as unknown as { __ATLAS_FROM__?: string }).__ATLAS_FROM__)
  || 'fcbh-reader';

export function AtlasLinkSpan({ link, lang, text, verseRef, extraClass }: Props) {
  const [open, setOpen] = useState(false);
  const [blurb, setBlurb] = useState<BlurbCacheEntry | undefined>(undefined);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    fetchBlurb(link.type, link.slug, lang).then((b) => {
      if (alive) setBlurb(b);
    });
    return () => {
      alive = false;
    };
  }, [open, link.type, link.slug, lang]);

  return (
    <span class={`atlas-link-host${extraClass ? ` ${extraClass}` : ''}`}>
      <button
        type="button"
        class="atlas-link"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-expanded={open}
      >
        {text}
      </button>
      {open ? (
        <span class="atlas-popover" role="dialog">
          <button
            type="button"
            class="atlas-popover-close"
            aria-label="Close"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          >
            ×
          </button>
          <span class="atlas-popover-eyebrow">
            {link.type} · {link.slug}
          </span>
          <span class="atlas-popover-text">
            {blurb?.text ?? 'Loading…'}
          </span>
          <a
            class="atlas-popover-link"
            href={atlasDeepLink(link.type, link.slug, lang, atlasOpts(verseRef))}
            target="_blank"
            rel="noopener noreferrer"
            onClick={recordAtlasJump}
          >
            Open full article →
          </a>
        </span>
      ) : null}
    </span>
  );
}
